import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const SoftwareIcons = () => {
  gsap.registerPlugin(ScrollTrigger);
  
  const wheelRef = useRef(null);
  const iconRefs = useRef([]);

  const icons = [
    
    { name: 'Adobe After Effects', src: '/Adobe_After_Effects_CC_icon.svg' },
    { name: 'Adobe Premiere Pro', src: '/Adobe_Premiere_Pro_CC_icon.svg' },

    { name: 'DaVinci Resolve', src: '/DaVinci_Resolve_Studio.png' },
    { name: 'Filmora', src: '/Filmora-logo-icon.svg' },
    { name: 'Adobe Lightroom', src: '/Adobe_Photoshop_Lightroom_CC_logo.svg' },
    { name: 'Adobe Photoshop', src: '/Adobe_Photoshop_CC_icon.svg' },
    { name: 'Adobe Illustrator', src: '/Adobe_Illustrator_CC_icon.svg' },
    { name: 'Final Cut Pro', src: '/final-cut-pro.png' },
  ];

  // Calculate angles dynamically with offset so first icon starts at top (12 o'clock)
  const angleOffset = -90; // shift so index 0 is at top
  const angles = icons.map((_, index) => (360 / icons.length) * index + angleOffset);

  // Get responsive radius
  const getRadius = () => {
    if (typeof window === 'undefined') return 100;
    if (window.innerWidth >= 1024) return 220;
    if (window.innerWidth >= 768) return 170;
    if (window.innerWidth >= 640) return 160;
    return 100;
  };

  useEffect(() => {
    if (!wheelRef.current) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    // Clean up existing triggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars?.trigger === "#home") {
        trigger.kill();
      }
    });

    const radius = getRadius();

    // Set initial positions - icons are perfectly vertical
    iconRefs.current.forEach((iconRef, index) => {
      if (iconRef) {
        const x = Math.cos((angles[index] * Math.PI) / 180) * radius;
        const y = Math.sin((angles[index] * Math.PI) / 180) * radius;
        
        // Set position with GSAP - NO ROTATION
        gsap.set(iconRef, { 
          x: x,
          y: y,
          rotation: 0,
          force3D: true
        });
      }
    });

    // Create scroll animation with 3D layering effect
    const scrollTrigger = ScrollTrigger.create({
      trigger: "#home",
      start: "top top",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        const currentRotation = self.progress * 120; // Reduced from 360 to 180 for slower rotation
        
        // 3D transform for the entire container - goes behind upcoming sections
        const container3D = self.progress * 500; // Move back in 3D space (increased intensity)
        gsap.set(wheelRef.current, {
          z: -container3D,
          force3D: true
        });
        
        iconRefs.current.forEach((iconRef, index) => {
          if (iconRef) {
            const newAngle = angles[index] + currentRotation;
            const x = Math.cos((newAngle * Math.PI) / 180) * radius;
            const y = Math.sin((newAngle * Math.PI) / 180) * radius;
            
            // Move icon to new position with 3D depth
            gsap.set(iconRef, { 
              x: x,
              y: y,
              z: -container3D * 0.8, // Icons also move back in 3D (increased)
              rotationX: self.progress * 15, // Add slight X rotation for depth
              rotation: 0, // ALWAYS ZERO - PERFECTLY VERTICAL
              force3D: true
            });
          }
        });
      },
      onLeaveBack: () => {
        // Reset 3D transform for container
        gsap.set(wheelRef.current, {
          z: 0,
          force3D: true
        });
        
        iconRefs.current.forEach((iconRef, index) => {
          if (iconRef) {
            const x = Math.cos((angles[index] * Math.PI) / 180) * radius;
            const y = Math.sin((angles[index] * Math.PI) / 180) * radius;
            gsap.set(iconRef, { 
              x: x,
              y: y,
              z: 0, // Reset 3D position
              rotationX: 0, // Reset X rotation
              rotation: 0,
              force3D: true
            });
          }
        });
      }
    });

    return () => {
      scrollTrigger.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Container - perfectly centered */}
      <div 
        ref={wheelRef}
        className="relative"
        style={{
          width: `${getRadius() * 2 + 100}px`,
          height: `${getRadius() * 2 + 100}px`,
          maxWidth: '400px',
          maxHeight: '400px',
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-57%, -50%)', // Perfectly centered
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Central dot */}
        {icons.map((icon, index) => (
          <div
            key={icon.name}
            ref={(el) => (iconRefs.current[index] = el)}
            className="absolute w-12 h-12 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-20 lg:h-20"
            style={{
              left: '50%',
              top: '50%',
              marginLeft: '-6px',
              marginTop: '-6px'
            }}
          >
            <motion.div
              className="w-full h-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <img
                src={icon.src}
                alt={icon.name}
                className="w-full h-full object-contain filter brightness-110 contrast-110 drop-shadow-lg"
              />
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SoftwareIcons;
